import React, { useState } from 'react';
import axios from 'axios';
import { Button, Card, CardBody, CardHeader, Spinner, Table } from 'reactstrap';
import { environment } from '../../../environment/environment';
import apiResponseHandler from '../../../utils/apiResponseHandler';

interface PaysoftActionsSectionProps {
    advanceId: number;
    sourceId: string | null;
}

const PROOF_OF_PAYMENT_EMAIL = 'devtools@jemhr.com';

const PaysoftActionsSection: React.FC<PaysoftActionsSectionProps> = ({ advanceId, sourceId }) => {
    const [lookupResults, setLookupResults] = useState<any[] | null>(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [popLoading, setPopLoading] = useState(false);

    const handleLookup = () => {
        setLookupLoading(true);
        setLookupResults(null);
        axios
            .post(`${environment.baseUrl}/paysoft/lookup/`, { reference: String(advanceId) })
            .then((res) => {
                setLookupResults(res.data.results || []);
            })
            .catch((error) => {
                apiResponseHandler.handleApiError('Paysoft Lookup Failed', error);
            })
            .finally(() => setLookupLoading(false));
    };

    const handleProofOfPayment = () => {
        if (!sourceId) return;
        setPopLoading(true);
        axios
            .post(`${environment.baseUrl}/paysoft/proof_of_payment/`, {
                transaction_id: sourceId,
                email: PROOF_OF_PAYMENT_EMAIL,
            })
            .then((res) => {
                try {
                    const { file_content, file_name } = res.data;
                    const blob = new Blob(
                        [Uint8Array.from(atob(file_content), (c) => c.charCodeAt(0))],
                        { type: 'application/pdf' }
                    );
                    const blobUrl = URL.createObjectURL(blob);
                    const anchor = document.createElement('a');
                    anchor.href = blobUrl;
                    anchor.download = file_name || 'proof_of_payment.pdf';
                    anchor.click();
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                } catch (e) {
                    apiResponseHandler.handleApiError('Failed to process PDF', e);
                }
            })
            .catch((error) => {
                apiResponseHandler.handleApiError('Proof of Payment Failed', error);
            })
            .finally(() => setPopLoading(false));
    };

    return (
        <Card className="shadow mt-4">
            <CardHeader className="font-weight-bold">Paysoft Actions</CardHeader>
            <CardBody className="p-0">

                {/* Lookup Payment */}
                <div className="p-3 border-bottom">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <h6 className="mb-1 font-weight-bold">Payment Lookup</h6>
                            <small className="text-muted">Query Paysoft for all transactions linked to this advance.</small>
                        </div>
                        <Button color="primary" size="sm" onClick={handleLookup} disabled={lookupLoading}>
                            {lookupLoading ? <Spinner size="sm" /> : 'Look Up'}
                        </Button>
                    </div>
                    {lookupResults && (
                        lookupResults.length === 0 ? (
                            <p className="text-muted mb-0 mt-2 small">No transactions found in Paysoft.</p>
                        ) : (
                            <Table responsive bordered size="sm" className="mt-2 mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Transaction ID</th>
                                        <th>Reference</th>
                                        <th>Amount</th>
                                        <th>Result</th>
                                        <th>Result Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lookupResults.map((r) => (
                                        <tr key={r.transaction_identifier}>
                                            <td>{r.transaction_identifier}</td>
                                            <td>{r.reference}</td>
                                            <td>{r.amount}</td>
                                            <td>{r.result}</td>
                                            <td>{r.result_date}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )
                    )}
                </div>

                {/* Proof of Payment */}
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h6 className="mb-1 font-weight-bold">Proof of Payment</h6>
                            <small className="text-muted">
                                {sourceId
                                    ? 'Download the Paysoft proof of payment PDF for this transaction.'
                                    : 'Not available — no Paysoft transaction ID on this advance.'}
                            </small>
                        </div>
                        <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={handleProofOfPayment}
                            disabled={popLoading || !sourceId}
                        >
                            {popLoading ? <Spinner size="sm" /> : 'Download PDF'}
                        </Button>
                    </div>
                </div>

            </CardBody>
        </Card>
    );
};

export default PaysoftActionsSection;
